/**
 * Pure helpers behind the Event owner picker and the new Event form.
 */

export type OwnerOptionBase = {
  kind: 'guild' | 'user'
  rowId: string
  /** Normalized text the search matches against. */
  searchText: string
}

export type OwnerSectionKey = 'results' | 'recent' | 'guilds' | 'personal'

// Combining diacritical marks left over after NFD normalization
const COMBINING_MARKS = new RegExp('[\\u0300-\\u036f]', 'g')

/** Lowercase and strip accents so "malmo" finds "Malmö". */
export const normalizeSearchText = (value: string) =>
  value.normalize('NFD').replace(COMBINING_MARKS, '').toLowerCase().trim()

/**
 * With a query: one "results" section of matches, in their original order.
 * Without: recently used owners first (most recent first), then the other
 * Guilds, then the personal option. Empty sections are left out.
 */
export const groupOwnerOptions = <T extends OwnerOptionBase>(
  options: T[],
  query: string,
  recentOwnerIds: string[]
): Array<{ key: OwnerSectionKey; options: T[] }> => {
  const normalizedQuery = normalizeSearchText(query)

  if (normalizedQuery) {
    const matches = options.filter((option) =>
      option.searchText.includes(normalizedQuery)
    )
    return matches.length ? [{ key: 'results', options: matches }] : []
  }

  const recent = recentOwnerIds
    .map((id) => options.find((option) => option.rowId === id))
    .filter((option): option is T => !!option)

  const sections: Array<{ key: OwnerSectionKey; options: T[] }> = [
    { key: 'recent', options: recent },
    {
      key: 'guilds',
      options: options.filter(
        (option) => option.kind === 'guild' && !recent.includes(option)
      ),
    },
    {
      key: 'personal',
      options: options.filter(
        (option) => option.kind === 'user' && !recent.includes(option)
      ),
    },
  ]

  return sections.filter((section) => section.options.length > 0)
}

/**
 * Who a new Event is preselected for: a Guild requested in the URL (by slug
 * or row ID), then the owner used last time, then the first Guild, then the
 * person themselves. Owners the person can no longer create Events for are
 * skipped.
 */
export const pickInitialEventOwnerId = ({
  requestedGuild,
  recentOwnerIds,
  guilds,
  userRowId,
}: {
  requestedGuild: string | null | undefined
  recentOwnerIds: string[]
  guilds: ReadonlyArray<{ rowId: string; slugId: string }>
  userRowId: string
}): string => {
  const requested = requestedGuild
    ? guilds.find(
        (guild) =>
          guild.slugId === requestedGuild || guild.rowId === requestedGuild
      )
    : undefined

  if (requested) {
    return requested.rowId
  }

  const recent = recentOwnerIds.find(
    (id) => id === userRowId || guilds.some((guild) => guild.rowId === id)
  )

  return recent ?? guilds[0]?.rowId ?? userRowId
}
